import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Holiday, HolidayForm, HolidayEditForm, ExportPublicHolidaysResponse } from '../../types/Holiday.interface';
import { downloadBlobFile } from '../../utils/fileUtils';
import { QueryKey as QueryKeys, QueryKey } from '../../enums/QueryKey';
import {
    getPublicHolidays,
    updateHoliday,
    createHoliday,
    deleteHoliday,
    uploadPublicHolidays,
    exportPublicHolidays,
} from '../../services/publicHolidays';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessNotification } from '../useDispatchSuccessNotification';
import { queryClient } from '../../services/queryClient';
import { useCreateRecord, useDeleteRecord, useUpdateRecord } from './crudOperations';

/** [holidays, isLoading, isError]. The third element matters wherever an EMPTY list would be read as a
 *  real answer: on error react-query reports isLoading false with no data, so `[]` there means "we could
 *  not ask", not "there are no holidays" (CALL-801 — the scenario Trainingstage prefill would otherwise
 *  count Christmas as a working day and save a training cost too high). */
export function usePublicHolidays(): [Holiday[], boolean, boolean] {
    const { data, isLoading, isError } = useQuery({
        queryKey: [QueryKey.PublicHolidays],
        queryFn: getPublicHolidays,
        refetchOnWindowFocus: false,
    });

    return [data || [], isLoading, isError];
}

export function useUpdateHoliday({ onSettled }: UseMutationOptions<Holiday, Error, HolidayEditForm>) {
    const { t } = useTranslation();

    return useUpdateRecord<Holiday, Error, HolidayEditForm>({
        updateRecordFn: updateHoliday,
        fetchRecordsQueryKey: [QueryKey.PublicHolidays],
        recordName: t('holiday.label'),
        onSettled,
    });
}

export function useCreateHoliday({ onSettled }: UseMutationOptions<Holiday, Error, HolidayForm>) {
    const { t } = useTranslation();

    return useCreateRecord<Holiday, Error, HolidayForm>({
        createRecordFn: createHoliday,
        fetchRecordsQueryKey: [QueryKey.PublicHolidays],
        recordName: t('holiday.label'),
        onSettled,
    });
}

export function useDeleteHoliday({ onSettled }: UseMutationOptions<{ success: boolean }, Error, number>) {
    const { t } = useTranslation();

    return useDeleteRecord<{ success: boolean }, Error, number>({
        deleteRecordFn: deleteHoliday,
        fetchRecordsQueryKey: [QueryKey.PublicHolidays],
        recordName: t('holiday.label'),
        onSettled,
    });
}

export const useUploadPublicHolidays = () => {
    const { t } = useTranslation();
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation<Holiday[], Error, File>({
        mutationFn: uploadPublicHolidays,
        mutationKey: [QueryKeys.PublicHolidays, 'upload'],
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.PublicHolidays]);
            dispatchSuccessNotification({ message: t('notification.fileUploaded') });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: t(error.message) });
        },
    });
};

export const useExportPublicHolidays = () => {
    const { t } = useTranslation();
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation<ExportPublicHolidaysResponse, AxiosError, void>({
        mutationFn: exportPublicHolidays,
        onError: (error) => {
            dispatchErrorNotification({ message: t(error.message) });
        },
        onSuccess: (data) => {
            dispatchSuccessNotification({ message: t('notification.fileGenerated') });
            downloadBlobFile(data);
        },
    });
};
